import { apiClient } from '../config/netConfig';
import {
  Space,
  SpaceFormData,
  SpaceQueryParams,
  ApiResponse,
  SpaceCoordinate,
  SpaceMarker,
  SpaceDrawing,
  SpaceType
} from '../types/space';

// API基础路径 - 指向spaces端点
const BASE_PATH = 'spaces';

class SpaceService {
  // 获取所有空间
  async getSpaces(params?: SpaceQueryParams): Promise<ApiResponse<Space[]>> {
    try {


      // 构建查询参数
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.pageSize) queryParams.append('limit', params.pageSize.toString());
      if (params?.keyword) queryParams.append('search', params.keyword);
      if (params?.type) queryParams.append('type', params.type);
      if (params?.status) queryParams.append('status', params.status);
      if (params?.parentId) queryParams.append('parentId', params.parentId);
      if (params?.tags && params.tags.length > 0) {
        params.tags.forEach(tagId => queryParams.append('tags', tagId));
      }

      const url = queryParams.toString() ? `${BASE_PATH}?${queryParams}` : BASE_PATH;


      const response = await apiClient.get(url);
      console.log('API响应:', response.data);

      // 处理后端响应格式
      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || [],
          pagination: response.data.pagination
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取空间列表失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间列表失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取空间列表失败'
      };
    }
  }

  // 获取单个空间
  async getSpace(id: string): Promise<ApiResponse<Space>> {
    try {

      console.log('空间ID:', id);

      const response = await apiClient.get(`${BASE_PATH}/${id}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '获取空间详情失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间详情失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '获取空间详情失败'
      };
    }
  }

  // 获取所有建筑
  async getBuildings(): Promise<ApiResponse<Space[]>> {
    try {
      // 使用getSpaces方法，通过type参数获取建筑类型的空间
      // 这样可以避免直接调用/buildings端点，提高接口的一致性
      const result = await this.getSpaces({ type: SpaceType.BUILDING });

      if (result.success) {
        return {
          success: true,
          data: result.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: result.error || '获取建筑列表失败'
        };
      }
    } catch (error: any) {
      console.error('获取建筑列表失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取建筑列表失败'
      };
    }
  }

  // 获取子空间
  async getChildren(parentId: string): Promise<ApiResponse<Space[]>> {
    try {

      console.log('父空间ID:', parentId);

      const response = await apiClient.get(`${BASE_PATH}/${parentId}/children`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取子空间失败'
        };
      }
    } catch (error: any) {
      console.error('获取子空间失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取子空间失败'
      };
    }
  }

  // 获取空间树
  async getSpaceTree(rootId?: string): Promise<ApiResponse<any>> {
    try {

      console.log('根空间ID:', rootId);

      const url = rootId ? `${BASE_PATH}/tree/${rootId}` : `${BASE_PATH}/tree`;
      const response = await apiClient.get(url);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取空间树失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间树失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取空间树失败'
      };
    }
  }

  // 创建空间
  async createSpace(data: SpaceFormData): Promise<ApiResponse<Space>> {
    try {


      const response = await apiClient.post(BASE_PATH, data);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '创建空间失败'
        };
      }
    } catch (error: any) {
      console.error('创建空间失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '创建空间失败'
      };
    }
  }

  // 更新空间
  async updateSpace(id: string, data: Partial<SpaceFormData>): Promise<ApiResponse<Space>> {
    try {

      console.log('空间ID:', id);
      console.log('更新数据:', data);

      const response = await apiClient.put(`${BASE_PATH}/${id}`, data);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: null,
          error: response.data.message || '更新空间失败'
        };
      }
    } catch (error: any) {
      console.error('更新空间失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || error.message || '更新空间失败'
      };
    }
  }

  // 删除空间
  async deleteSpace(id: string): Promise<ApiResponse<boolean>> {
    try {

      console.log('空间ID:', id);

      const response = await apiClient.delete(`${BASE_PATH}/${id}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: true
        };
      } else {
        return {
          success: false,
          data: false,
          error: response.data.message || '删除空间失败'
        };
      }
    } catch (error: any) {
      console.error('删除空间失败:', error);
      return {
        success: false,
        data: false,
        error: error.response?.data?.message || error.message || '删除空间失败'
      };
    }
  }

  // 批量删除空间
  async batchDeleteSpaces(ids: string[]): Promise<ApiResponse<{ successful: number, total: number, failed: number }>> {
    try {

      console.log('删除ID列表:', ids);

      const response = await apiClient.post(`${BASE_PATH}/batch-delete`, { ids });
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data
        };
      } else {
        return {
          success: false,
          data: { successful: 0, total: ids.length, failed: ids.length },
          error: response.data.message || '批量删除空间失败'
        };
      }
    } catch (error: any) {
      console.error('批量删除空间失败:', error);
      return {
        success: false,
        data: { successful: 0, total: ids.length, failed: ids.length },
        error: error.response?.data?.message || error.message || '批量删除空间失败'
      };
    }
  }

  // 获取空间坐标
  async getSpaceCoordinates(spaceId: string): Promise<ApiResponse<SpaceCoordinate[]>> {
    try {

      console.log('空间ID:', spaceId);

      const response = await apiClient.get(`/coordinates?spaceId=${spaceId}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取空间坐标失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间坐标失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取空间坐标失败'
      };
    }
  }

  // 获取空间标记
  async getSpaceMarkers(spaceId: string): Promise<ApiResponse<SpaceMarker[]>> {
    try {

      console.log('空间ID:', spaceId);

      const response = await apiClient.get(`/markers?spaceId=${spaceId}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取空间标记失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间标记失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取空间标记失败'
      };
    }
  }

  // 获取空间图纸
  async getSpaceDrawings(spaceId: string): Promise<ApiResponse<SpaceDrawing[]>> {
    try {

      console.log('空间ID:', spaceId);

      const response = await apiClient.get(`/drawings?spaceId=${spaceId}`);
      console.log('API响应:', response.data);

      if (response.data.success) {
        return {
          success: true,
          data: response.data.data || []
        };
      } else {
        return {
          success: false,
          data: [],
          error: response.data.message || '获取空间图纸失败'
        };
      }
    } catch (error: any) {
      console.error('获取空间图纸失败:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.message || error.message || '获取空间图纸失败'
      };
    }
  }

  // 获取空间统计数据
  async getSpaceStatistics(params?: SpaceQueryParams): Promise<ApiResponse<{
    total: number;
    buildings: number;
    zones: number;
    spaces: number;
    active: number;
    inactive: number;
  }>> {
    try {
      let url = `/${BASE_PATH}/statistics`;

      // 如果有搜索参数，添加到URL中
      if (params) {
        const queryParams = new URLSearchParams();

        if (params.keyword) queryParams.append('search', params.keyword);
        if (params.type) queryParams.append('type', params.type);
        if (params.status) queryParams.append('status', params.status);
        if (params.parentId) queryParams.append('parentId', params.parentId);
        if (params.tags && params.tags.length > 0) {
          params.tags.forEach(tagId => queryParams.append('tags', tagId));
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }
      }

      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data.data  // 修复：获取嵌套的data字段
      };
    } catch (error: any) {
      console.error('获取空间统计数据失败:', error);
      return {
        success: false,
        data: null,
        error: error.response?.data?.message || '获取空间统计数据失败'
      };
    }
  }
}

// 创建服务实例
const spaceService = new SpaceService();

// 导出实例
export default spaceService;
